import { differenceInDays, differenceInHours } from 'date-fns'
import { useApplicationStore } from 'src/aplication-store'
import useSWR from 'swr'
import axios from 'axios'
import type { Color } from 'src/theme'
import { hasBeenUpdated, markAsUpdated } from 'src/utils/ticket-cache'

const FIVE_MINUTES_IN_MS = 300000
const FOUR_MINUTES_IN_MS = 240000

type Info = {
    id: number
    dataProximaAcao: number
    status: 'CONCLUIDO' | 'ANDAMENTO' | 'AGUARDANDO' | 'SUCESSO' | 'INSUCESSO'
    nomeEmpresa: string
    periodoTempoLimite: 'DIA' | 'HORA'
    tempoLimite: number
}

export type InfoResponse = {
    ticketInfo: Info | undefined
    isLoading: boolean
    alertColor: Color
    statusColor: Color
}

export const useTicketInfo = (ticketId: number, ticketInfoProp?: InfoResponse): InfoResponse => {
    const [token, user, onTicketUpdate] = useApplicationStore(({ token, user, onTicketUpdate }) => [
        token,
        user,
        onTicketUpdate
    ])
    const baseURL = `${location.protocol}//${location.host}/api`

    const invalid = !token || !!ticketInfoProp?.ticketInfo?.id

    const { data, isLoading } = useSWR<Info>(
        invalid ? null : [`${baseURL}/evento/${ticketId}/flyweight`, token],
        ([path, token]: [string, string]) => {
            return axios
                .get(path, {
                    headers: {
                        Authorization: token,
                        'Content-Type': 'application/json'
                    }
                })
                .then((res) => res.data)
        },
        {
            refreshInterval: FIVE_MINUTES_IN_MS,
            dedupingInterval: FOUR_MINUTES_IN_MS
        }
    )

    const cached = hasBeenUpdated(ticketId)

    if (data && !cached && isTicketClosed(data?.status)) {
        const payload = { domain: user?.domain || '', ticketId, status: data.status || '' }

        if (payload.domain && payload.ticketId && payload.status) {
            onTicketUpdate(payload)
            markAsUpdated(ticketId)
        }
    }

    const ticketInfo = data || ticketInfoProp?.ticketInfo
    const alertColor = getTicketAlertColor(ticketInfo)
    const statusColor = getTicketStatusColor(ticketInfo)

    return { ticketInfo, isLoading, alertColor, statusColor }
}

export const useTicketsInfo = (ticketsId: number[], executeFetch: boolean): InfoResponse[] => {
    const response: InfoResponse[] = []

    const [token, user, onTicketUpdate] = useApplicationStore(({ token, user, onTicketUpdate }) => [
        token,
        user,
        onTicketUpdate
    ])

    const baseURL = `${location.protocol}//${location.host}/api`

    const invalid = !token || !ticketsId?.length || !executeFetch
    const queryParams = ticketsId?.map((ticketId) => `id=${ticketId}`).join('&')

    const { data, isLoading } = useSWR<Info[]>(
        invalid ? null : [`${baseURL}/evento/flyweight?${queryParams}`, token],
        ([path, token]: [string, string]) => {
            return axios
                .get(path, {
                    headers: {
                        Authorization: token,
                        'Content-Type': 'application/json'
                    }
                })
                .then((res) => res.data)
        },
        {
            refreshInterval: FIVE_MINUTES_IN_MS,
            dedupingInterval: FOUR_MINUTES_IN_MS
        }
    )

    if (!ticketsId?.length || !data?.length) {
        return response
    }

    for (const ticket of data) {
        const cached = hasBeenUpdated(ticket.id)

        if (!cached && isTicketClosed(ticket?.status)) {
            const payload = { domain: user?.domain || '', ticketId: ticket.id, status: ticket.status || '' }

            if (payload.domain && payload.ticketId && payload.status) {
                onTicketUpdate(payload)
                markAsUpdated(ticket.id)
            }
        }

        const alertColor = getTicketAlertColor(ticket)
        const statusColor = getTicketStatusColor(ticket)
        const ticketInfo = ticket

        response.push({ ticketInfo, isLoading, alertColor, statusColor })
    }

    return response
}

function getTicketAlertColor(info?: Pick<Info, 'periodoTempoLimite' | 'tempoLimite' | 'dataProximaAcao'>): Color {
    const yellow = 'warning'
    const red = 'error'
    const green = 'success'

    if (!info) return green

    const tempoLimite = info.tempoLimite
    const periodoTempoLimite = info.periodoTempoLimite
    const isHours = periodoTempoLimite === 'HORA'

    const now = new Date()
    const nextAction: Date = new Date(info.dataProximaAcao)

    const diffInDays = differenceInDays(nextAction, now)

    if (diffInDays === 0) {
        if (isHours) {
            const diffInHour = differenceInHours(nextAction, now)
            if (isLate(diffInHour, tempoLimite)) {
                return red
            } else {
                return yellow
            }
        } else {
            return yellow
        }
    }

    if (diffInDays < 0) {
        if (isHours) {
            return red
        }

        if (isLate(diffInDays, tempoLimite)) {
            return red
        } else {
            return yellow
        }
    }

    return green
}

function getTicketStatusColor(info?: Pick<Info, 'status'>): Color {
    if (info?.status === 'AGUARDANDO') return 'primary'
    if (info?.status === 'ANDAMENTO') return 'info'
    if (info?.status === 'SUCESSO') return 'secondary'
    if (info?.status === 'INSUCESSO') return 'error'
    return 'primary'
}

function isLate(diff: number, limit: number) {
    const result = diff + limit
    if (result < 0) {
        return true
    }
    return false
}

function isTicketClosed(status?: string): boolean {
    if (status) {
        return ['CONCLUIDO', 'CANCELADO', 'INSUCESSO', 'SUCESSO'].includes(status)
    }
    return false
}
